// Hypothetical market mechanics; availability and corporate-action terms vary by venue.
export const executionLessons = [
  {
    id: 'bid-ask-spread', moduleId: 'execution',
    title: '沒有手續費，交易就沒有成本嗎？', subtitle: '用中間價看買賣價差與往返成本', minutes: 7, tag: '成交成本',
    objectives: ['區分買賣價差、相對價差與單邊成交偏離', '在報價不變的假設下計算一次買進再賣出的價差成本'],
    paragraphs: [
      '最佳委買是當下買方願付的最高報價，最佳委賣是賣方願收的最低報價；兩者之差是報價買賣價差。主動買進通常要面對委賣，主動賣出則面對委買。中間價是兩者的平均，可用作比較基準，但不是保證存在足夠數量能成交的價格。',
      '比較不同價格的商品時，可以用「委賣減委買，再除以中間價」計算一種相對價差口徑，必須標明分母。若報價對稱且不變，買在委賣相對中間價付出半個價差，賣在委買再付出半個；一次往返合計一個完整價差，不是每一邊都另扣一整個價差。',
      '這只是靜態報價的練習。真實交易還受可成交數量、價格變動、交易場所以及路由影響，實際成交可能較好也可能較差；不能把報價價差直接當成所有人的實際成本。手續費、稅與其他費用也要另計，且已反映在實際成交價中的價差，不應再從成交損益重複扣除。',
    ],
    example: {
      title: '假設：同一組報價下買進，再立即賣出',
      steps: ['示意市場最佳委買 79.8 元、最佳委賣 80.2 元，兩邊都有足夠的 200 股可成交，整個過程報價不變，忽略稅與手續費。', '中間價為（79.8 + 80.2）÷ 2 = 80 元；完整價差 0.4 元，相對中間價為 0.4 ÷ 80 = 0.5%。', '以 80.2 元買 200 股付出 16,040 元，再以 79.8 元全數賣出收到 15,960 元，往返差額為 80 元。相對買進支出的損失約為 0.4988%，分母不同，所以不恰等於 0.5%。', '相對中間價，買進多付 40 元、賣出少收 40 元，合計仍是 80 元；若已用實際收付款計算損益，不再額外扣一次 80 元。'],
      conclusion: '零手續費不會自動消除價差；計算時也別把已反映的成本扣兩次。',
    },
    pitfalls: ['把中間價當成保證可交易的價格，忽略兩邊掛單數量。', '同時從實際買賣價算損益，又再扣一遍完整買賣價差。'],
    glossary: [{ term: '中間價', definition: '最佳委買與最佳委賣的算術平均，常作比較基準，不保證可以成交。' }, { term: '相對價差', definition: '把價差除以指定價格基準得到的比例；比較前要核對分母與資料時間。' }],
    takeaway: '先寫清楚價格、數量與分母，再把價差和額外費用分開計算。',
    exercise: '沿用例子的報價，把數量改成 50 股；在同樣靜態假設下計算往返差額，再解釋為何不能由小額的結果推定大額也能用同價成交。',
    quiz: { question: '已用實際買進支出與賣出收入算出損益，應如何處理其中已反映的買賣價差？', options: ['買進和賣出各再扣一個完整價差', '不再重複扣除，另核對尚未包含的費用與稅', '只要沒有手續費，就把價差造成的差額加回'], answer: 1, explanation: '實際成交收付款已包含買賣價格的差異。尚未包含的費稅應另計，但把同一價差再扣一次會高估成本。' },
    sources: [
      { label: '市場先生｜五檔報價與買賣價格的主題閱讀', url: 'https://rich01.com/stock-program-basic-01/', kind: 'reading' },
      { label: 'SEC｜買賣價差與交易成本', url: 'https://www.investor.gov/introduction-investing/general-resources/news-alerts/alerts-bulletins/investor-bulletins-24', kind: 'official' },
    ],
  },
  {
    id: 'market-limit-orders', moduleId: 'execution',
    title: '限價 40.2 元，為什麼可能只買到一部分？', subtitle: '價格上限、逐筆成交與未成交餘額', minutes: 7, tag: '委託條件',
    objectives: ['分開檢查價格條件、成交數量與委託狀態', '理解限價可以成交得更好，也可能留下未成交部分'],
    paragraphs: [
      '買進限價是接受的最高成交價格，賣出限價是最低價格，不是要求每股剛好在指定價格成交。價格符合時，仍要有足夠對手數量並滿足撮合規則；同價排隊或報價消失，都可能讓委託尚未成交。市價單則沒有自行設定這個價格界線，可能在數個價位成交。',
      '可以把一次委託拆成三個問題：券商是否收到、已成交多少、剩餘部分現在是等待還是已取消。不同市場、時段、商品與券商提供的委託及有效期限不同，不能把美股或台股某個畫面的功能當成所有地方都有。市價也可能遇到暫停、缺乏對手或其他交易限制，不能承諾任何情況都立刻全成。',
      '若選擇取消剩餘委託，需要等取消結果，而不是把按鈕點下去就當作全數消失。傳送取消與市場撮合之間，可能再有成交；之後若重新送單，應先核對最新成交與剩餘量，避免把原來仍有效的數量又送一次。這是狀態核對，不是推薦特定下單策略。',
    ],
    example: {
      title: '假設：同一張委託簿，兩種價格條件',
      steps: ['示意市場委賣有 60 股掛 40.1 元、140 股掛 40.3 元。假設無其他委託、報價不變，允許部分成交，忽略稅費；兩個方案分別從同一初始狀態計算。', '限價 40.2 元買 100 股，只能先買到 60 股，成交價 40.1 元。另 40 股不會自動接受 40.3 元；假設本例規則允許保留，它們繼續等待。', '改看市價買 100 股的獨立方案：60 股在 40.1 元、40 股在 40.3 元成交，總支出 4,018 元，平均成交價 40.18 元。', '限價方案的回報應記成「已成交 60 股、剩餘 40 股」，而不是因為送單 100 股就記成持有 100 股。剩餘是否取消，另以回報為準。'],
      conclusion: '限價約束每筆成交，不是只看最後平均價；市價方案平均雖低於 40.2 元，其中的 40.3 元成交仍不符合該買進限價。',
    },
    pitfalls: ['以為買進限價越高，就必須以該限價成交，忽略可能有更好的成交價格。', '把委託已受理、取消已送出與全數成交或取消成功當成相同狀態。'],
    glossary: [{ term: '部分成交', definition: '委託中只有部分數量完成撮合，其餘仍須依有效期限及交易規則處理。' }, { term: '平均成交價', definition: '各筆成交價格乘數量後加總，再除以成交總數量；未成交部分不包含在內。' }],
    takeaway: '下單後核對的是成交明細與未成交餘額，不只是委託成功畫面。',
    exercise: '在紙上畫出「送出 100 股 → 成交 60 股 → 送出取消 → 再成交 10 股 → 剩餘取消成功」，填上最後持股與取消股數，並列出核對重新送單前所需的回報。',
    quiz: { question: '例中的市價方案平均 40.18 元，低於買進限價 40.2 元，為何限價方案仍不能照樣買滿？', options: ['系統只看平均價，所以一定是漏單', '限價只決定記帳價格，成交價格沒有限制', '限價約束每筆成交，市價方案中的 40.3 元超過界線'], answer: 2, explanation: '買進限價不是成交均價上限。即使所有成交加權後低於 40.2 元，仍不能接受其中 40.3 元的那一筆；剩餘數量需要其他符合價格的對手。' },
    sources: [
      { label: '市場先生｜市價與限價的主題閱讀', url: 'https://rich01.com/vs_60/', kind: 'reading' },
      { label: 'SEC｜委託種類、價格限制與成交風險', url: 'https://www.investor.gov/introduction-investing/general-resources/news-alerts/alerts-bulletins/investor-bulletins-14', kind: 'official' },
    ],
  },
  {
    id: 'stop-orders', moduleId: 'execution',
    title: '停損價是觸發點，不是保證出場價', subtitle: '停損市價與停損限價的不同結果', minutes: 8, tag: '條件委託',
    objectives: ['區分觸發條件與觸發後送出的委託', '理解跳空、短暫波動與未成交可能造成的結果'],
    paragraphs: [
      '一般停損市價單（stop-market）先等待條件，達到設定的停損價後轉成市價委託；觸發價不等於成交價。停損限價單（stop-limit）則在觸發後轉成限價委託，另有價格界線。名稱含「停損」不代表損失必定止於設定金額，也不代表只可用於同一種買賣情境。',
      '先查清楚功能由交易所還是券商提供、用成交價或何種報價判定、哪些時段生效，以及有效期限、交易暫停和公司行動如何處理。這些條件可能不同，本課不假設所有台股、美股或券商都提供相同單型。短暫價格變動可能觸發後又反彈，跳空則可能直接越過你設定的價位。',
      '停損市價單承擔成交價格偏離觸發價的風險；停損限價單保留價格限制，卻可能無法成交而繼續持有。兩者解決的問題不同，沒有同時保證價格與全數成交的魔法。評估時應列出「觸發但價格不理想」和「觸發卻未成交」兩種情境，而不是只把設定畫面當成最大損失證明。',
    ],
    example: {
      title: '假設：價格跳過門檻，會發生什麼？',
      steps: ['假想商品買入成本每股 100 元，設賣出停損門檻 91 元。假設該券商以符合條件的成交價觸發，隔夜後第一筆成交為 84 元，滿足觸發規則。', '停損市價方案觸發後，假設可取得的實際成交是 83.5 元；忽略稅費，損失為 16.5%，不是由門檻推算的 9%。這是示意結果，不是成交價預測。', '另一獨立方案使用相同門檻 91 元，但觸發後的賣出限價是 89 元。若之後一直沒有願以 89 元以上承接的對手，委託可能持續未成交，仍承擔持有風險。'],
      conclusion: '觸發成功不等於損失被封頂；加入限價也會增加無法成交的可能。',
    },
    pitfalls: ['把停損門檻當成保證成交價格，忽略跳空、流動性和交易限制。', '以為停損限價單只會改善成交價，卻不會改變成交機會。'],
    glossary: [{ term: '停損市價單', definition: '達到約定觸發條件後轉為市價委託，成交價格可能偏離觸發價。' }, { term: '停損限價單', definition: '達到觸發條件後轉為限價委託，限制價格但不保證成交。' }],
    takeaway: '先讀觸發規則，再讀觸發後的委託；兩個階段都可能有風險。',
    exercise: '只閱讀、不送出委託：找一份券商說明，記下觸發依據、生效時段、有效期限與觸發後單型；若沒有提供這項功能，也如實註記。',
    quiz: { question: '賣出停損限價單已觸發，但市場上所有可成交買價都低於賣出限價，可能怎樣？', options: ['保持未成交，持股仍繼續承擔風險', '系統必須替你找到限價以上的買方', '一定自動取消限價並以任何價格賣出'], answer: 0, explanation: '限價保留最低接受價格；沒有符合價格與數量的對手，就可能無法成交。是否等待、取消或到期，另依委託規則處理。' },
    sources: [
      { label: '市場先生｜不同停損委託的主題閱讀', url: 'https://rich01.com/ib-trail-limit/', kind: 'reading' },
      { label: 'FINRA｜停損單在波動市場的成交與未成交風險', url: 'https://www.finra.org/sites/default/files/notice_other_file_ref/Regulatory-Notice-16-19.pdf', kind: 'official' },
      { label: 'SEC｜不同券商與市場的觸發條件差異', url: 'https://www.investor.gov/introduction-investing/investing-basics/glossary/order-types', kind: 'official' },
    ],
  },
  {
    id: 'stock-splits', moduleId: 'execution',
    title: '股票一拆三，為什麼不是資產變三倍？', subtitle: '股數、每股價格與持股比例一起調整', minutes: 6, tag: '公司行動',
    objectives: ['同時計算分割前後股數、理論價格與持有總值', '辨認拆分調整、歷史價格與實際市場漲跌的差別'],
    paragraphs: [
      '純股票分割把同一家公司權益分成更多股，原股東股數依比例增加，公司總股數也一起增加；沒有其他變化時，持股比例不因這個動作增加。反向分割則往相反方向調整股數與每股價格。它們都不能單靠改變每股單位創造營運獲利。',
      '計算理論調整時，若一股變三股，股數乘三、價格除三，同一獲利對應的 EPS 也按可比口徑調整。這是單位變換，不是市場承諾實際交易一定停在理論價。若直接把未調整的分割前價格接到分割後價格，圖上可能看似暴跌；讀走勢前要確認平台是否已處理分割。',
      '實務還要看生效日期、股票入帳與交易安排、未成交委託如何處理，以及不足一股的權益如何安排。零碎股可能依公告給付現金或採其他方式，不能自己假定必定能保留小數股。分割與股利、減資的法律及會計處理也不相同，要依公司公告辨認實際發生的行動。',
    ],
    example: {
      title: '假設：1 股變 3 股，先只看機械調整',
      steps: ['持有 70 股，每股 96 元，總值 6,720 元。公司進行一股變三股的純分割，忽略稅費、其他公司行動與市場價格變動。', '分割後持有 210 股，理論每股價格 32 元；210 × 32 仍為 6,720 元。若公司總股數也變三倍，原持股比例不變。', '假設可比較年度 EPS 原為 12 元，按分割後單位調整為 4 元；本益比由 96 ÷ 12 = 8 倍，變成 32 ÷ 4 = 8 倍，沒有因低單價自動變便宜。'],
      conclusion: '多了股數，也要同時調整每股數字；之後的市場漲跌是另一件事。',
    },
    pitfalls: ['只看股數增加就當成獲利，或只看每股價格下降就當成虧損。', '把未調整的歷史價格與分割後價格直接相接，錯算報酬與圖形。'],
    glossary: [{ term: '純股票分割', definition: '依比例增加股份單位，在沒有其他變動時不改變各股東持股比例。' }, { term: '分割調整', definition: '為維持前後可比性，將歷史每股價格、EPS 等資料換算到一致股份單位。' }],
    takeaway: '先看股份單位有沒有改，再判斷價格變動代表什麼。',
    exercise: '用同一筆 70 股、每股 96 元，改成假想的每兩股合成一股，計算股數與理論價格；再列出若原持股無法整除，必須查公告的事項。',
    quiz: { question: '純粹一股變三股，沒有其他變動時，哪一項應維持不變？', options: ['每股價格與每股 EPS 都不調整', '持股比例與理論持有總值', '只有股數改變，總市值必定增為三倍'], answer: 1, explanation: '個人與公司股數同比例增加，每股理論價格相應調整，持股比例與理論總值不變；實際成交仍會受到後續市場資訊影響。' },
    sources: [
      { label: '市場先生｜股票分割的主題閱讀', url: 'https://rich01.com/what-is-stock-splite/', kind: 'reading' },
      { label: 'FINRA｜股票分割與持股比例', url: 'https://syndication.finra.org/content/corporate-actions-public-companies-what-you-should-know', kind: 'official' },
      { label: 'SEC｜反向分割及零碎股處理', url: 'https://www.investor.gov/introduction-investing/investing-basics/glossary/reverse-stock-splits', kind: 'official' },
    ],
  },
  {
    id: 'rights-issues', moduleId: 'execution',
    title: '認購價比市價低，增資就一定有賺嗎？', subtitle: '新資金、持股稀釋與認購權的理論價值', minutes: 8, tag: '現金增資',
    objectives: ['把增資帶入的新現金與新增股數同時納入計算', '區分持股比例稀釋、認購權與實際可成交價格'],
    paragraphs: [
      '現金增資發行新股，公司取得資金，同時有更多股份分享權益。若符合條件的原股東可按比例認購，是否參與會影響持股比例；但每次發行的原股東、員工與其他認購安排可能不同，不能把一個固定比例當成所有增資的通則。認購還需支付新資金，低認購價不是免費配股。',
      '本課限定一個簡化模型：每 n 股舊股可按 S 元認 1 股新股，增資前含權股價 P，S 低於 P，新股全部完成認購，沒有其他股利、費稅或價值變化，收到現金以等額加入股權價值。理論除權價 T =（n × P + S）÷（n + 1）；每一舊股附帶權益的理論價值為 P − T。這是條件式推算，不是市場報價或保證收益。',
      '未認購者的持股比例可能下降；若認購權可轉讓且確實能出售，權益的出售收入才可能補償部分經濟影響。不能假定所有市場的權利都能交易，也不能把放棄認購視為必定收到補償。需查認購資格、期限、繳款、權利可否轉讓、未認購的處理及資金用途；參與與否都不是只憑認購折價就能決定。',
    ],
    example: {
      title: '假設：每 4 舊股可按 24 元認 1 新股',
      steps: ['公司原有 100,000 股，含權股價 40 元，股權總值 4,000,000 元。依本課簡化假設，全數發行 25,000 新股、每股收 24 元，取得現金 600,000 元。', '理論除權價 T =（4 × 40 + 24）÷ 5 = 36.8 元，也等於 4,600,000 ÷ 125,000。每舊股附帶權益理論值 40 − 36.8 = 3.2 元；四份權益合計 12.8 元，等於 36.8 − 24。', '原持有 400 股者占 0.4%。全額認購 100 股需再付 2,400 元，之後 500 股占 0.4%，理論值 18,400 元；扣掉新投入的 2,400 元，仍對應原本 16,000 元，不是憑空多賺。', '若完全不認購，400 股占比降至 0.32%，理論股票價值為 14,720 元。400 份權益理論值共 1,280 元，但只有在允許轉讓且能成交等條件下才可能變現；直接失效不等於會自動拿到這筆錢。'],
      conclusion: '同時算股數、現金與權利；理論折價不能取代對發行條件的查核。',
    },
    pitfalls: ['只算新股認購價與增資前價格的差額，漏掉除權調整及必須追加的現金。', '把持股比例稀釋直接等同固定幅度的投資損失，忽略新資金用途、權利與市場變動。'],
    glossary: [{ term: '理論除權價', definition: '依明確假設把增資前股權價值與新現金分配到新總股數的價格，不是保證成交價。' }, { term: '認購權', definition: '依發行條件認購新股的權利；可否轉讓、有效期限及失效處理須查公告。' }],
    takeaway: '增資需要把追加現金也放進帳裡，不能只用認購折價判定賺賠。',
    exercise: '沿用本例，分別列出「全數認購」「不認購且權利失效」「假設權利可按理論值出售」的期末股數、現金收付與理論總價值，明確標示各自假設。',
    quiz: { question: '本例每 4 舊股以 24 元認 1 新股，含權價 40 元；在全部簡化假設成立時，理論除權價是多少？', options: ['40 元，因為原股東買入成本不變', '24 元，所有股票都改成認購價', '36.8 元，須將原股權價值與新現金一起除以新股數'], answer: 2, explanation: '（4 × 40 + 24）÷ 5 = 36.8 元。此值依本課限定條件計算，實際市場價格、權利交易和發行安排可能不同。' },
    sources: [
      { label: '市場先生｜現金增資的主題閱讀', url: 'https://rich01.com/blog-post_23-7/', kind: 'reading' },
      { label: '證交所｜除權參考價計算公式', url: 'https://wwwc.twse.com.tw/zh/announcement/ex-right/twt49u.html', kind: 'official' },
    ],
  },
  {
    id: 'share-buybacks', moduleId: 'execution',
    title: '回購讓 EPS 上升，公司就一定更有價值嗎？', subtitle: '股數減少時，別漏掉公司付出的現金', minutes: 8, tag: '庫藏股',
    objectives: ['同時檢查回購現金、流通股數與每股盈餘', '分辨機械性 EPS 增加與真正的經濟價值改變'],
    paragraphs: [
      '股份回購是公司買回自己的股份，可能持為庫藏股或依規定註銷，會使用資金並影響流通在外股數。宣布額度不等於已執行，買回多少也不必等於總股數淨減少多少：同時發行新股或員工獎酬等因素可能抵銷。要把宣布、實際買回與期末股數分開讀。',
      '基本 EPS 一般使用歸屬普通股股東的獲利除以期間加權平均流通股數，不是隨便拿期末股數相除。獲利不變時，分母變小可讓 EPS 機械性上升；但回購支出的現金不會仍留在公司。若用借款支付，還要考慮利息與負債；用既有現金也可能減少利息收入或其他資金用途。',
      '回購的經濟影響與支付價格、公司價值、資金用途及財務條件有關，不能只憑 EPS 或回購新聞判定必然有利。下面假設現金以一元對一元計入股權價值，且回購不影響其餘業務，用來看清現金與股數的同步變化。真實公司價值與未來獲利仍需重新評估，不會被這個算式保證。',
    ],
    example: {
      title: '假設：股數少了，現金也少了',
      steps: ['公司有 100,000 股，假設整體股權價值 1,000,000 元、每股 10 元，普通股股東年度獲利為 50,000 元；原 EPS = 0.5 元。公司備有足夠現金。', '在年度一開始以每股 10 元買回 10,000 股，支付 100,000 元，之後沒有其他股數變動。本例忽略稅費、資金利息與業務影響，全年獲利仍設為 50,000 元。', '全年加權平均流通股數變成 90,000 股，EPS = 50,000 ÷ 90,000，約 0.5556 元，比 0.5 元高約 11.11%。', '但現金流出後，在本例假設下股權總值為 900,000 元，除以 90,000 股仍為每股 10 元。EPS 上升本身沒有讓每股價值自動增加。'],
      conclusion: '每股盈餘的分母縮小，不等於現金或營運能力憑空增加。',
    },
    pitfalls: ['把宣布回購上限直接當成已買回股數，忽略執行情況及同期新股發行。', '只看 EPS 上升，漏掉回購價格、支付現金、借款成本與期間加權股數。'],
    glossary: [{ term: '庫藏股', definition: '公司買回而尚未依安排註銷或重新處理的自身股份，與一般對外投資資產的概念不同。' }, { term: '加權平均流通股數', definition: '依股份在期間內流通的時間加權計算，回購發生在年初或年末對當期 EPS 的影響不同。' }],
    takeaway: '看回購，要同時讀現金、股數與獲利；EPS 一個數字無法代替價值判斷。',
    exercise: '沿用例子，假設同樣回購改在年中完成、前後各占半年，計算加權平均股數與 EPS；再寫下為何期末股數相同，全年 EPS 仍可能不同。',
    quiz: { question: '獲利不變、回購使加權平均股數下降並提高 EPS，能直接推出什麼？', options: ['每股分攤的獲利變多，但仍需檢查付出的現金與其他影響', '公司的年度總獲利一定增加', '回購不使用公司資源，因此一定創造同額價值'], answer: 0, explanation: 'EPS 可以因分母下降而上升；總獲利未必改變，且公司已付出現金或承擔融資成本，不能只由 EPS 判定價值增加。' },
    sources: [
      { label: '市場先生｜庫藏股與股份回購的主題閱讀', url: 'https://rich01.com/blog-pos-24/', kind: 'reading' },
      { label: 'IFRS Foundation｜IAS 33 每股盈餘與加權平均股數', url: 'https://www.ifrs.org/issued-standards/list-of-standards/ias-33-earnings-per-share/', kind: 'official' },
      { label: 'Microsoft 申報財報｜回購現金與股份變動揭露', url: 'https://www.sec.gov/Archives/edgar/data/789019/000119312526436149/msft-ex99_1.htm', kind: 'official' },
    ],
  },
];
